import React, {
	useRef,
	useEffect,
	useMemo,
	useState,
	useCallback
} from 'react';
import { useRequestData } from 'wsm-common-data';
import Slider from 'react-slick';
import PropTypes from 'prop-types';
import { Image } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { galleryTrackingEvents } from '../../utilities/tracking';
import './thumbnails.scss';
import { REACT_SLICK_SPEED } from '../../global-constants';

const SLIDE_TO_SHOW = 6;
const ANIMATION_SPEED = REACT_SLICK_SPEED + 50; // Larger than react-slick's speed

function Thumbnails({
	carouselIndex,
	isMobile,
	setCarouselIndex,
	modalSliderRef,
	imgTitle,
	isHorizontalLayout = false
}) {
	const { imagesToDisplay = [] } = useSelector((state) => state.media);
	const { windowId, widgetName } = useRequestData();
	const isInitialMount = useRef(true);
	const containerRef = useRef(null);
	const slider = useRef();
	const [slidesToShow, setSlidesToShow] = useState(null);
	const imagesCount = imagesToDisplay.length;
	const [isAddedTabIndex, setIsAddedTabIndex] = useState(false);
	const animationEndCallback = useRef(null); // Animation lock like react-slick

	const settings = useMemo(
		() => ({
			infinite: false,
			slidesToScroll: 1,
			vertical: !isMobile && !isHorizontalLayout,
			lazyLoad: false,
			focusOnSelect: false,
			variableWidth: isMobile || isHorizontalLayout,
			initialSlide: carouselIndex,
			accessibility: false, // Disable react-slick's keyboard handling, we handle it ourselves
			arrows: false,
			draggable: true,
			swipe: true,
			speed: REACT_SLICK_SPEED
		}),
		[carouselIndex, isMobile, isHorizontalLayout]
	);

	const mergedSettings = {
		...settings,
		...(slidesToShow && { slidesToShow })
	};

	const getSlideToDisplay = () => {
		if (!containerRef.current) {
			return SLIDE_TO_SHOW;
		}

		const thumbWrapperHeight = document.querySelector(
			'#gallery-modal-thumbnail-wrapper'
		).clientHeight;
		const slideHeight =
			containerRef.current.querySelector('.slick-slide').clientHeight;

		const _slidesToShow = thumbWrapperHeight / slideHeight;

		return _slidesToShow > 3
			? Math.floor(_slidesToShow)
			: Math.round(_slidesToShow);
	};

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const updateSlidesToShow = useCallback(
		window._.debounce(() => {
			const calculatedSlidesToShow = getSlideToDisplay();
			const limitedSlidesToShow = Math.min(
				calculatedSlidesToShow,
				SLIDE_TO_SHOW,
				imagesCount
			);

			setSlidesToShow(limitedSlidesToShow);
		}, 100),
		[]
	);

	useEffect(() => {
		// sync with carousel
		if (isInitialMount.current) {
			isInitialMount.current = false;
		} else {
			slider.current.slickGoTo(carouselIndex);
		}
	}, [carouselIndex, isMobile]);

	useEffect(() => {
		if (!isMobile) {
			if (!containerRef.current) return;

			const wrappers = containerRef.current.querySelectorAll(
				'div.thumbnail-img-wrapper'
			);
			wrappers.forEach((wrapper) => {
				if (
					isAddedTabIndex &&
					wrapper.getAttribute('tabindex') !== '0'
				) {
					wrapper.setAttribute('tabindex', '0');
				}
				if (
					!isAddedTabIndex &&
					wrapper.getAttribute('tabindex') !== '-1'
				) {
					wrapper.setAttribute('tabindex', '-1');
				}
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAddedTabIndex, isMobile]);

	const onClickSlide = (index) => {
		galleryTrackingEvents.clickThumbnail(widgetName, windowId, {
			currentItem: carouselIndex + 1,
			totalItems: imagesCount
		});
		setCarouselIndex(index);
		modalSliderRef.current.slickGoTo(index);
	};

	// Navigation handler with animation lock (like react-slick's slideHandler)
	const navigateToSlide = useCallback(
		(index) => {
			// Block navigation if animation is in progress (waitForAnimate behavior)
			if (animationEndCallback.current) {
				return; // Ignore keypress during animation
			}

			setCarouselIndex(index);
			modalSliderRef.current.slickGoTo(index, true);

			const thumbnailButtons = containerRef.current.querySelectorAll(
				'div.thumbnail-img-wrapper'
			);
			requestAnimationFrame(() => {
				thumbnailButtons[index]?.focus({ preventScroll: true });
			}); // Create animation lock and schedule its release after animation completes
			animationEndCallback.current = setTimeout(() => {
				animationEndCallback.current = null; // Release lock
			}, ANIMATION_SPEED);
		},
		[modalSliderRef, setCarouselIndex]
	);

	const onFocusSlide = (index) => {
		// When Tab focuses a thumbnail, navigate to it
		if (index !== carouselIndex) {
			setCarouselIndex(index);
			modalSliderRef.current.slickGoTo(index, true);
		}
	};

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const handleKeyDown = useCallback(
		(event) => {
			// Extract key immediately before debounce to avoid React event pooling issues
			const { key } = event;

			// Only handle arrow keys
			if (!['ArrowUp', 'ArrowDown'].includes(key)) {
				return;
			}

			// Prevent default scrolling and stop event propagation immediately
			event.preventDefault();
			event.stopPropagation();

			// Use carouselIndex from the component state, not the index parameter
			// because the index parameter can be stale from the closure
			let nextIndex = carouselIndex;

			// Both up/down and left/right arrows work for navigation
			// Up/Left arrows go to previous, Down/Right arrows go to next
			if (key === 'ArrowUp') {
				// Move to previous thumbnail (with wrap-around)
				nextIndex =
					carouselIndex > 0
						? carouselIndex - 1
						: imagesToDisplay.length - 1;
			} else if (key === 'ArrowDown') {
				// Move to next thumbnail (with wrap-around)
				nextIndex =
					carouselIndex < imagesToDisplay.length - 1
						? carouselIndex + 1
						: 0;
			}

			// Only proceed if the index changed
			if (nextIndex !== carouselIndex) {
				navigateToSlide(nextIndex);
			}
		},
		[carouselIndex, imagesToDisplay.length, navigateToSlide]
	);

	return (
		<div
			ref={containerRef}
			className={`slider-container thumbnails-container ${
				isMobile ? 'mobile' : ''
			} ${isMobile && carouselIndex === 0 ? 'ml-4' : ''} ${
				isHorizontalLayout ? 'horizontal-layout' : ''
			}`}
			onKeyDown={handleKeyDown}
			onFocus={(event) => {
				// Check if focus came from outside the container (fresh entry via Tab)
				const focusCameFromOutside = !containerRef.current?.contains(
					event.relatedTarget
				);

				if (!isAddedTabIndex || focusCameFromOutside) {
					setIsAddedTabIndex(true);
					const thumbnailButtons =
						containerRef.current.querySelectorAll(
							'div.thumbnail-img-wrapper'
						);
					thumbnailButtons[carouselIndex]?.focus();
				}
			}}
			onBlur={(event) => {
				// Check if focus is leaving the container entirely
				const focusLeavingContainer = !containerRef.current?.contains(
					event.relatedTarget
				);

				if (focusLeavingContainer && isAddedTabIndex) {
					setIsAddedTabIndex(false);
				}
			}}
			role="tablist"
			aria-label="Vehicle image thumbnails"
			tabIndex={isAddedTabIndex ? -1 : 0}
		>
			<Slider ref={slider} {...mergedSettings}>
				{imagesToDisplay.map((image, index) => {
					return isMobile ? (
						<button
							onClick={() => onClickSlide(index)}
							className="btn-unstyled"
							type="button"
							key={image.src}
						>
							<Image
								alt={`${
									index + 1
								} per ${imagesCount} vehicle thumbnail images`}
								key={`thumb-${image.thumbnail}`}
								className={`slider-thumbnail ${
									index === carouselIndex ? 'active' : ''
								} slider-thumbnail--${
									isMobile ? 'mobile mr-4' : 'desktop'
								}`}
								src={image.thumbnail}
								title={imgTitle}
							/>
						</button>
					) : (
						<button
							onClick={() => onClickSlide(index)}
							onFocus={() => onFocusSlide(index)}
							type="button"
							className={
								isHorizontalLayout
									? 'btn-unstyled'
									: 'btn-unstyled mb-sm-4'
							}
							key={image.src}
							aria-label={`Thumbnail ${
								index + 1
							} of ${imagesCount}`}
						>
							<div
								className="thumbnail-img-wrapper"
								role="tab"
								aria-label={`Thumbnail ${
									index + 1
								} of ${imagesCount}`}
							>
								<Image
									alt={`${
										index + 1
									} per ${imagesCount} vehicle thumbnail images`}
									key={`thumb-${image.thumbnail}`}
									className={`slider-thumbnail w-100 ${
										index === carouselIndex ? 'active' : ''
									} slider-thumbnail--${
										isHorizontalLayout
											? 'horizontal'
											: 'desktop'
									}`}
									src={image.thumbnail}
									onLoad={updateSlidesToShow}
									title={imgTitle}
								/>
							</div>
						</button>
					);
				})}
			</Slider>
		</div>
	);
}

Thumbnails.propTypes = {
	isMobile: PropTypes.bool,
	carouselIndex: PropTypes.number.isRequired,
	setCarouselIndex: PropTypes.func.isRequired,
	modalSliderRef: PropTypes.oneOfType([
		PropTypes.func,
		PropTypes.shape({ current: PropTypes.instanceOf(Element) })
	]),
	imgTitle: PropTypes.string,
	isHorizontalLayout: PropTypes.bool
};

export default Thumbnails;
